import { useId, useRef } from 'react';
import { BENCHMARKS } from '../education/benchmarks.js';
import type { UnitSystem } from '../units/units.js';
import { benchmarksFor, eui as euiUnit } from '../units/units.js';
import { formatEui } from '../ui/format.js';
import { useWidth } from './useWidth.js';

/**
 * Where this figure stands, on a scale that goes as far as laboratories actually
 * go. The i2SL mean of 531 is the right-hand end because anchoring at, say, 200
 * would quietly flatter every result.
 */
const STANDARD_W = 720;
/**
 * Below this the 720-unit scale is shown at under two-thirds size, and its 9 px
 * ticks fall under 6 px. It is drawn at the width it is shown instead: the same
 * track, the same two landmarks at either end, and type at its stated size.
 */
const COMPACT_BELOW = 480;
const H = 62;
const PAD = 4;
const TRACK_Y = 30;
const MAX = BENCHMARKS.i2sl.mean;

interface Props {
  readonly eui: number;
  readonly units: UnitSystem;
}

export function BenchmarkScale({ eui, units }: Props) {
  const titleId = useId();
  const figureRef = useRef<HTMLElement>(null);
  const measured = useWidth(figureRef, COMPACT_BELOW);
  const W = measured.compact ? measured.width : STANDARD_W;
  if (eui <= 0) return null;

  // Positions stay in IP, like the engine; only the words on the scale follow
  // the reader's system. They used to print the IP landmarks in both — a bare
  // 25–30 and 531 beside a value in kWh/m²/yr.
  const landmarks = benchmarksFor(units);
  const x = (value: number) => PAD + (Math.min(value, MAX) / MAX) * (W - PAD * 2);
  const here = x(eui);
  const overrun = eui > MAX;

  return (
    <figure className="benchmark" aria-labelledby={titleId} ref={figureRef}>
      <figcaption className="visually-hidden" id={titleId}>
        This programme against the net zero range and the i2SL laboratory benchmark mean
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="benchmark__svg"
        data-layout={measured.compact ? 'compact' : 'standard'}
        role="img"
        aria-labelledby={titleId}
      >
        <rect x={PAD} y={TRACK_Y} width={W - PAD * 2} height={7} rx={3.5} className="benchmark__track" />

        <rect
          x={x(BENCHMARKS.netZero.low)}
          y={TRACK_Y - 3}
          width={Math.max(x(BENCHMARKS.netZero.high) - x(BENCHMARKS.netZero.low), 3)}
          height={13}
          rx={3}
          className="benchmark__zone"
        />
        <text x={x(BENCHMARKS.netZero.high) + 8} y={TRACK_Y - 8} className="benchmark__tick">
          Net zero {landmarks.netZeroLow}–{landmarks.netZeroHigh}
        </text>

        <line
          x1={x(MAX)} y1={TRACK_Y - 5} x2={x(MAX)} y2={TRACK_Y + 12}
          className="benchmark__rule"
        />
        <text x={x(MAX) - 8} y={TRACK_Y - 8} className="benchmark__tick" textAnchor="end">
          i2SL mean {landmarks.i2slLabMean.toLocaleString('en-US')}
        </text>

        <g transform={`translate(${here}, 0)`}>
          <circle cx={0} cy={TRACK_Y + 3.5} r={7} className="benchmark__here" />
          <text
            x={0} y={TRACK_Y + 30}
            className="benchmark__value"
            textAnchor={here > W - 120 ? 'end' : here < 90 ? 'start' : 'middle'}
          >
            {overrun ? '≥ ' : ''}{formatEui(eui, units)} {euiUnit.label(units)}
          </text>
        </g>
      </svg>
    </figure>
  );
}
